/// FOURJS_START_COPYRIGHT(D,2025)
/// Property of Four Js*
/// (c) Copyright Four Js 2025, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("DOMElementHelper", [], function (context, cls) {
  /**
   * Helper class providing static methods to manipulate and interact with DOM elements.
   * Replaces all previous `element.js` and `elementMobile.js` prototype extensions.
   * @namespace classes.DOMElementHelper
   */
  class DOMElementHelper {
    /**
     * Adds an event listener to a DOM element.
     * Supports event namespaces (e.g., `"click.myNamespace"`).
     *
     * @param {Element} elem - Target DOM element.
     * @param {string} type - Event type, optionally including namespace (e.g. `"click.ns"`).
     * @param {Function} callback - Function to execute when the event is triggered.
     * @returns {void}
     */
    static on(elem, type, callback) {
      if (!elem) {
        return;
      }

      const classedEventRE = /([^.]+)+(\..+)?/;
      elem.__gbcRegisteredEvents ??= {};
      const t = classedEventRE.exec(type || ""),
        event = t && t[1],
        passive = event === "wheel" || event === "touchstart" || event === "touchmove",
        eventClass = t && t[2];
      if (event) {
        elem.__gbcRegisteredEvents[event] ??= { __default: [] };
        const registered = elem.__gbcRegisteredEvents[event];
        const key = eventClass || "__default";
        registered[key] ??= [];
        registered[key].push(callback);
        elem.addEventListener(
          event,
          callback,
          passive
            ? {
                passive: true
              }
            : false
        );
      }
    }

    /**
     * Removes one or more event listeners from a DOM element.
     * Can remove by namespace or event type.
     *
     * @param {Element} elem - Target DOM element.
     * @param {string} type - Event type or namespaced event (e.g. `"click.ns"`).
     * @returns {void}
     */
    static off(elem, type) {
      if (!elem) {
        return;
      }

      const classedEventRE = /([^.]+)+(\..+)?/;
      elem.__gbcRegisteredEvents = elem.__gbcRegisteredEvents || {};
      const t = classedEventRE.exec(type || "") || [],
        event = t[1],
        eventClass = t[2];
      if (event) {
        const registered = elem.__gbcRegisteredEvents[event] || {};
        if (eventClass) {
          if (registered[eventClass]) {
            while (registered[eventClass].length) {
              elem.removeEventListener(event, registered[eventClass].pop());
            }
          }
        } else {
          const keys = Object.keys(registered);
          for (const element of keys) {
            while (registered[element].length) {
              elem.removeEventListener(event, registered[element].pop());
            }
          }
        }
      }
    }

    /**
     * Retrieves an attribute value from an element and converts it to an integer.
     *
     * @param {Element} elem - Target DOM element.
     * @param {string} name - Attribute name.
     * @returns {?number} Parsed integer value, or `null` if not a valid number.
     */
    static getIntAttribute(elem, name) {
      const result = Number.parseInt(elem.getAttribute(name), 10);
      return Number.isNaN(result) ? null : result;
    }

    /**
     * Sets focus on a DOM element, optionally preventing automatic scrolling.
     * Automatically skips focus when a system modal or GBC filter input is active.
     *
     * @param {Element} elem - Target element to focus.
     * @param {boolean} [preventScroll=false] - Whether to prevent automatic scrolling.
     * @returns {void}
     */
    static domFocus(elem, preventScroll = false) {
      const activeElement = document.activeElement;
      if (elem === activeElement) {
        return;
      }

      // if gbc chrome bar filter has focus you should not change it
      const gbcFilterInputHasFocus = Boolean(activeElement) && activeElement.classList.contains("gbc_FilterWidget__input");
      if (gbcFilterInputHasFocus) {
        return;
      }

      elem.focus({
        preventScroll: preventScroll
      });
    }

    /**
     * Checks whether the element has a parent node of the specified type.
     *
     * @param {Element} elem - Target DOM element.
     * @param {string} nodeName - Node name to match (e.g. `"DIV"`, `"TABLE"`).
     * @returns {boolean} True if a parent of that type exists.
     */
    static hasParentOfType(elem, nodeName) {
      let el = elem;
      while (el.parentNode !== null) {
        el = el.parentNode;
        if (el.nodeName === nodeName) {
          return true;
        }
      }
      return false;
    }

    /**
     * Sets the cursor (selection) position inside an input or textarea element.
     *
     * @param {HTMLInputElement|HTMLTextAreaElement} elem - Input element.
     * @param {number} pos - Start position.
     * @param {number} [pos2=pos] - End position (optional, defaults to same as `pos`).
     * @returns {void}
     */
    static setCursorPosition(elem, pos, pos2) {
      if (!pos2 || pos2 === 0) {
        pos2 = pos;
      }
      try {
        if (!cls.DOMElementHelper.hasParentOfType(elem, "#document-fragment") && elem.setSelectionRange) {
          elem.setSelectionRange(pos, pos2);
        }
      } catch (e) {
        console.log(e.toString());
      }
    }

    /**
     * Retrieves the current selected text within an input or textarea element.
     *
     * @param {HTMLInputElement|HTMLTextAreaElement} elem - Input element.
     * @returns {?string} The selected text, or `null` if unavailable.
     */
    static getSelectionText(elem) {
      if (!elem) {
        return null;
      }

      try {
        return elem.value.substring(elem.selectionStart, elem.selectionEnd);
      } catch (e) {
        console.log(e.toString());
        return null;
      }
    }

    /**
     * Replaces the current element with another one in the DOM.
     *
     * @deprecated Prefer using the native `Element.replaceWith()` method.
     * @param {Element} elem - Element to replace.
     * @param {Element} replacement - New element to insert.
     * @returns {void}
     */
    static replaceWith(elem, replacement) {
      elem.parentNode.insertBefore(replacement, elem);
      elem.remove();
    }

    /**
     * Prepends a child element to the beginning of the parent's children list.
     *
     * @param {Element} elem - Parent DOM element.
     * @param {Element} child - Child element to insert.
     * @returns {void}
     */
    static prependChild(elem, child) {
      elem.insertBefore(child, elem.children[0]);
    }

    /**
     * Inserts an element at a specific position inside a parent node.
     *
     * @param {Element} elem - Element to insert.
     * @param {number} index - Target index (0-based).
     * @param {HTMLElement} parentNode - Parent node.
     * @returns {void}
     */
    static insertAt(elem, index, parentNode) {
      const parentChildren = parentNode.children;

      if (index === 0) {
        if (parentChildren.length) {
          cls.DOMElementHelper.prependChild(parentNode, elem);
        } else {
          parentNode.appendChild(elem);
        }
      } else {
        const where = Boolean(index) && parentChildren[index];
        if (where) {
          if (where !== elem) {
            where.before(elem);
          }
        } else {
          parentNode.appendChild(elem);
        }
      }
    }

    /**
     * Inserts an element directly after a reference node.
     *
     * @param {Element} elem - Element to insert.
     * @param {HTMLElement} refNode - Reference node.
     * @returns {void}
     */
    static insertAfter(elem, refNode) {
      if (refNode && refNode.parentNode) {
        refNode.parentNode.insertBefore(elem, refNode.nextSibling);
      }
    }

    /**
     * Removes all child nodes from the specified element.
     *
     * @param {Element} elem - Target DOM element.
     * @returns {void}
     */
    static empty(elem) {
      while (elem.firstChild) {
        elem.firstChild.remove();
      }
    }

    /**
     * Finds the nearest parent element with the given CSS class.
     *
     * @param {Element} elem - Starting element.
     * @param {string} className - CSS class to match.
     * @returns {?HTMLElement} Matching parent element, or `null` if none found.
     */
    static parent(elem, className) {
      let parent = elem.parentNode;
      while (parent) {
        if (parent.nodeType === Node.ELEMENT_NODE && parent.classList.contains(className)) {
          break;
        }
        parent = parent.parentNode;
      }
      return parent;
    }

    /**
     * Returns the element itself if it has the specified class,
     * otherwise searches its ancestors for the class.
     *
     * @param {Element} elem - Target DOM element.
     * @param {string} className - Class to look for.
     * @returns {?HTMLElement} Matching element or ancestor, or `null` if not found.
     */
    static elementOrParent(elem, className) {
      if (elem.classList.contains(className)) {
        return elem;
      }

      return cls.DOMElementHelper.parent(elem, className);
    }

    /**
     * Checks whether the element is the same as, or a child of, another element.
     *
     * @param {Element} elem - Element to test.
     * @param {HTMLElement} refElem - Reference element.
     * @returns {boolean} True if `elem` is the same or inside `refElem`.
     */
    static isElementOrChildOf(elem, refElem) {
      let current = elem;
      while (current) {
        if (current.nodeType === Node.ELEMENT_NODE && current === refElem) {
          return true;
        }
        current = current.parentNode;
      }
      return false;
    }

    /**
     * Determines if an element is attached to the document's DOM tree.
     *
     * @param {Element} elem - DOM element.
     * @returns {boolean} True if the element is currently in the DOM.
     */
    static isInDOM(elem) {
      let found = false;
      let parent = elem.parentNode;
      while (!found) {
        if (!parent) {
          break;
        }
        if (parent === window.document.body) {
          found = true;
          break;
        }
        parent = parent.parentNode;
      }
      return found;
    }

    /**
     * Finds the first child element with a given CSS class.
     *
     * @param {Element} elem - Parent element.
     * @param {string} className - CSS class to match.
     * @returns {?HTMLElement} The matching child element, or `null`.
     */
    static child(elem, className) {
      const children = elem.children;
      let found = null;
      for (const element of children) {
        if (element.classList.contains(className)) {
          found = element;
          break;
        }
      }
      return found;
    }

    /**
     * Finds the first child element matching a specific tag name.
     *
     * @param {Element} elem - Parent element.
     * @param {string} tagName - Tag name (case-insensitive).
     * @returns {?HTMLElement} The matching child element, or `null`.
     */
    static childTag(elem, tagName) {
      const children = elem.children;
      let found = null;
      for (const element of children) {
        if (element.tagName.toLowerCase() === tagName.toLowerCase()) {
          found = element;
          break;
        }
      }
      return found;
    }

    /**
     * Returns all child elements except the specified one.
     *
     * @param {Element} elem - Parent element.
     * @param {Element} item - Child element to exclude.
     * @returns {HTMLElement[]} Array of all other children.
     */
    static childrenExcept(elem, item) {
      const children = elem.children;
      const result = [];
      for (const element of children) {
        if (element !== item) {
          result.push(element);
        }
      }
      return result;
    }

    /**
     * Toggles one or two classes on an element based on a switch value.
     * If `switcher` is omitted, toggles `cssClass1` normally.
     *
     * @param {Element} elem - Target element.
     * @param {string} cssClass1 - Class to toggle or activate.
     * @param {string|boolean} cssClass2 - Second class or boolean switch.
     * @param {boolean} [switcher] - Explicit toggle condition.
     * @returns {void}
     */
    static toggleClass(elem, cssClass1, cssClass2, switcher) {
      if (!elem) {
        return;
      }
      if (typeof switcher === "undefined") {
        switcher = cssClass2;

        if (switcher === true || switcher === false) {
          elem.classList.toggle(cssClass1, switcher);
        } else {
          elem.classList.toggle(cssClass1);
        }
      } else if (switcher) {
        elem.classList.remove(cssClass2);
        elem.classList.add(cssClass1);
      } else {
        elem.classList.remove(cssClass1);
        elem.classList.add(cssClass2);
      }
    }

    /**
     * Adds or removes classes based on a condition.
     * Equivalent to a ternary operation for CSS states.
     *
     * @param {Element} elem - Target element.
     * @param {boolean} condition - Condition to evaluate.
     * @param {string} whenTrue - Class to apply when `condition` is true.
     * @param {?string} [whenFalse] - Optional class to apply when `condition` is false.
     * @returns {void}
     */
    static toggleClasses(elem, condition, whenTrue, whenFalse) {
      if (!elem) {
        return;
      }
      if (condition) {
        if (whenFalse) {
          elem.classList.remove(whenFalse);
        }
        elem.classList.add(whenTrue);
      } else {
        elem.classList.remove(whenTrue);
        if (whenFalse) {
          elem.classList.add(whenFalse);
        }
      }
    }

    /**
     * Wrap HTML elements into a LI element
     * @param {string} className
     * @param {HTMLElement} elements
     * @return {HTMLLIElement}
     * @private
     */
    static wrapElementIntoListItem(className, ...elements) {
      const li = document.createElement("li");
      li.className = className;
      li.append(...elements);
      return li;
    }

    /**
     * Returns the 0-based index of an element among its sibling elements.
     *
     * @param {Element} elem - DOM element.
     * @returns {number} Index of element, or -1 if not found.
     */
    static index(elem) {
      let index = 0;
      let i = 0;
      const parent = elem.parentNode;
      const children = parent.childNodes;
      const len = children.length;
      for (; i < len; i++) {
        const item = children[i];
        if (item === elem) {
          return index;
        }
        if (item.nodeType === Node.ELEMENT_NODE) {
          index++;
        }
      }
      return -1;
    }

    /**
     * Updates a DOM attribute based on the provided value.
     * Removes the attribute if the value is `null`, `undefined`, or empty.
     *
     * @param {Element} elem - Target element.
     * @param {string} qualifiedName - Attribute name.
     * @param {*=} value - New value to set, or `null`/`undefined` to remove.
     * @returns {void}
     */
    static updateAttribute(elem, qualifiedName, value) {
      if (!elem) {
        return;
      }
      if (value === null || typeof value === "undefined" || value === "") {
        elem.removeAttribute(qualifiedName);
      } else {
        elem.setAttribute(qualifiedName, value.toString());
      }
    }

    /**
     * Updates an ARIA attribute on an element (`aria-*` prefix is added automatically).
     * Removes the attribute if the value is `null`, `undefined`, or empty.
     *
     * @param {Element} elem - Target element.
     * @param {string} qualifiedName - ARIA attribute name (without prefix).
     * @param {*=} value - Value to set, or `null`/`undefined` to remove.
     * @returns {void}
     */
    static updateAriaAttribute(elem, qualifiedName, value) {
      if (!elem) {
        return;
      }
      if (value === null || typeof value === "undefined" || value === "") {
        elem.removeAttribute(`aria-${qualifiedName}`);
      } else {
        elem.setAttribute(`aria-${qualifiedName}`, value.toString());
      }
    }

    /**
     * Adds or updates a QA attribute on an element.
     * Automatically prefixes with `data-gqa-` or `data-aui-name`.
     *
     * @param {Element} elem - Target element.
     * @param {string} qualifiedName - QA attribute name (without prefix).
     * @param {string} value - Attribute value.
     * @returns {void}
     */
    static updateQAAttribute(elem, qualifiedName, value) {
      if (qualifiedName === "name") {
        cls.DOMElementHelper.updateAttribute(elem, "data-aui-name", value);
      } else {
        cls.DOMElementHelper.updateAttribute(elem, `data-gqa-${qualifiedName}`, value);
      }
    }

    /**
     * Registers swipe gesture detection on a touch device.
     *
     * @param {Element} elem - Target element.
     * @param {string} ctx - Namespace identifier.
     * @param {Function} swipeCallback - Callback executed when a swipe is detected. Receives direction ("left", "right", "top", "bottom").
     * @param {Object} [options] - Gesture configuration.
     * @param {string[]} [options.direction=["left","right","top","bottom"]] - Directions to listen for.
     * @param {number} [options.velocity=0.2] - Minimum swipe velocity (px/ms).
     * @param {number} [options.distance=0.1] - Minimum fraction of element size to trigger.
     * @param {boolean} [options.debounce=false] - Whether to debounce move/start events.
     * @param {Element|Element[]} [options.ignore] - Elements to ignore for gesture start.
     * @param {Function} [options.startCallback] - Executed on gesture start.
     * @param {Function} [options.moveCallback] - Executed during gesture movement.
     * @param {Function} [options.endCallback] - Executed when gesture ends.
     * @returns {void}
     */
    static onSwipe(elem, ctx, swipeCallback, options) {
      if (!cls.WindowHelper.isTouchDevice()) {
        return;
      }

      options = typeof options === "object" ? options : {};

      // watched swipe directions
      const direction = options.direction ? options.direction : ["left", "right", "top", "bottom"];
      const watchedDirection = {};
      for (const element of direction) {
        watchedDirection[element] = true;
      }
      let _touchStartX = null;
      let _touchStartY = null;
      let _touchStartTime = null;

      const unifyTouchEvent = function (evt) {
        return evt.changedTouches ? evt.changedTouches[0] : evt;
      };

      /** Touch/Mouse Events **/
      const endSwipe = function (evt) {
        // @TODO : Crash here !
        if (evt.touches && evt.touches.length > 0) {
          // we don't manage multiples touch
          return;
        }
        // on touch/mouse release unbind move/up events
        cls.DOMElementHelper.off(elem, "mouseup.swipe_" + ctx);
        cls.DOMElementHelper.off(elem, "touchend.swipe_" + ctx);
        cls.DOMElementHelper.off(elem, "mouseleave.swipe_" + ctx);
        cls.DOMElementHelper.off(elem, "touchleave.swipe_" + ctx);
        if (options.moveCallback) {
          cls.DOMElementHelper.off(elem, "mousemove.swipe_" + ctx);
          cls.DOMElementHelper.off(elem, "touchmove.swipe_" + ctx);
        }

        let swipedVelocity = 0;
        let swipedFraction = 0;

        if (_touchStartX && _touchStartY && evt) {
          // swipe direction, distance and velocity calculation
          const xUp = unifyTouchEvent(evt).clientX;
          const yUp = unifyTouchEvent(evt).clientY;
          const xDiff = _touchStartX - xUp;
          const yDiff = _touchStartY - yUp;
          const xIndex = Math.sign(xDiff);
          const yIndex = Math.sign(yDiff);

          // Get most significant direction
          const absXMove = Math.abs(xDiff);
          const absYMove = Math.abs(yDiff);
          const isXSwipe = absXMove > absYMove;
          const maxMove = Math.max(absXMove, absYMove);
          if (maxMove > 15) {
            // exclude micro gestures and clicks from calculation
            const totalDistance = isXSwipe ? elem.clientWidth : elem.clientHeight;
            swipedFraction = (maxMove / totalDistance).toFixed(2);
          } else if (maxMove > 0) {
            swipedFraction = 0.05;
          }

          const duration = new Date().getTime() - _touchStartTime;
          const velocity = options.velocity ? options.velocity : 0.2;
          const distanceToSwipe = options.distance ? options.distance : 0.1;
          swipedVelocity = maxMove / duration;

          // swipe if minimum velocity of gesture (distance/duration of swipe) or swiped 50% of viewport width
          if (swipedVelocity >= velocity || swipedFraction > distanceToSwipe) {
            swipedFraction = 1 - swipedFraction;
            if (isXSwipe) {
              if (xIndex > 0) {
                /* left swipe */
                if (watchedDirection.left) {
                  swipeCallback("left");
                }
              } else if (watchedDirection.right) {
                swipeCallback("right");
              }
            } else if (yIndex > 0) {
              if (watchedDirection.top) {
                swipeCallback("top");
              }
            } else if (watchedDirection.bottom) {
              swipeCallback("bottom");
            }
          }
        }

        if (options.endCallback) {
          options.endCallback(evt, swipedVelocity, swipedFraction);
        }

        // reset values
        _touchStartX = null;
        _touchStartY = null;
        _touchStartTime = null;
      };

      cls.DOMElementHelper.offSwipe(elem, ctx);

      let endHandler = endSwipe;
      // touch/mouse down event callback
      const startSwipe = function (evt) {
        if (options.ignore) {
          if (!Array.isArray(options.ignore)) {
            options.ignore = [options.ignore];
          }
          if (
            options.ignore.find(function (item) {
              return evt.target && cls.DOMElementHelper.isElementOrChildOf(evt.target, item);
            })
          ) {
            return;
          }
        }
        if (evt.ctrlKey === false && (!evt.touches || evt.touches.length === 1)) {
          // do not swipe if CTRL key pressed and do not add additional bindings if multiple fingers
          if (evt.cancelable) {
            // prevent text selection
            evt.preventDefault();
          }

          cls.DOMElementHelper.on(elem, "mouseup.swipe_" + ctx, endHandler);
          cls.DOMElementHelper.on(elem, "touchend.swipe_" + ctx, endHandler);
          cls.DOMElementHelper.on(elem, "mouseleave.swipe_" + ctx, endHandler);
          cls.DOMElementHelper.on(elem, "touchleave.swipe_" + ctx, endHandler);

          if (options.moveCallback) {
            // touch/mouse move callback
            const moveHandler = function (evt) {
              if (evt.cancelable) {
                // to avoid browser back native
                evt.preventDefault();
              }
              options.moveCallback(evt, _touchStartX, _touchStartY);
            };
            cls.DOMElementHelper.on(elem, "mousemove.swipe_" + ctx, moveHandler);
            cls.DOMElementHelper.on(elem, "touchmove.swipe_" + ctx, moveHandler);
          }

          _touchStartX = unifyTouchEvent(evt).clientX;
          _touchStartY = unifyTouchEvent(evt).clientY;
          _touchStartTime = new Date().getTime();
          if (options.startCallback) {
            options.startCallback(evt);
          }
        }
      };

      let startHandler = startSwipe;
      if (options.debounce) {
        startHandler = cls.FunctionHelper.debounce(startSwipe.bind(elem));
        endHandler = cls.FunctionHelper.debounce(endSwipe.bind(elem));
      }
      cls.DOMElementHelper.on(elem, "mousedown.swipe_" + ctx, startHandler);
      cls.DOMElementHelper.on(elem, "touchstart.swipe_" + ctx, startHandler);
    }

    /**
     * Unregisters swipe gesture listeners for a given namespace.
     *
     * @param {Element} elem - Target element.
     * @param {string} ctx - Namespace identifier used when registering.
     * @returns {void}
     */
    static offSwipe(elem, ctx) {
      cls.DOMElementHelper.off(elem, "mousemove.swipe_" + ctx);
      cls.DOMElementHelper.off(elem, "touchmove.swipe_" + ctx);
      cls.DOMElementHelper.off(elem, "mousedown.swipe_" + ctx);
      cls.DOMElementHelper.off(elem, "touchstart.swipe_" + ctx);
      cls.DOMElementHelper.off(elem, "mouseup.swipe_" + ctx);
      cls.DOMElementHelper.off(elem, "touchend.swipe_" + ctx);
      cls.DOMElementHelper.off(elem, "mouseleave.swipe_" + ctx);
      cls.DOMElementHelper.off(elem, "touchleave.swipe_" + ctx);
    }

    /**
     * Registers a long-touch gesture handler (touch held for a specified duration).
     *
     * @param {Element} elem - Target element.
     * @param {string} ctx - Namespace identifier for this event.
     * @param {Function} callback - Callback executed when a long touch is detected.
     * @param {Object} [options] - Additional configuration.
     * @param {number} [options.touchDuration=500] - Minimum duration (ms) to trigger callback.
     * @param {boolean} [options.preventDefault=true] - Prevent default behavior on touchstart.
     * @returns {void}
     */
    static onLongTouch(elem, ctx, callback, options) {
      if (!cls.WindowHelper.isTouchDevice()) {
        return;
      }
      let touchDuration = options?.touchDuration ?? 500;
      let preventDefault = options?.preventDefault ?? true;
      let timer = null;

      cls.DOMElementHelper.on(elem, "touchstart.longTouch_" + ctx, function (event) {
        if (preventDefault) {
          cls.EventHelper.preventCancelableDefault(event);
        }
        timer = setTimeout(
          function () {
            callback(event);
          }.bind(elem),
          touchDuration
        );
      });

      cls.DOMElementHelper.on(elem, "touchend.longTouch_" + ctx, function () {
        if (timer) {
          clearTimeout(timer);
        }
      });
    }

    /**
     * Unregisters long-touch gesture handlers for a given namespace.
     *
     * @param {Element} elem - Target element.
     * @param {string} ctx - Namespace identifier.
     * @returns {void}
     */
    static offLongTouch(elem, ctx) {
      if (cls.WindowHelper.isTouchDevice()) {
        cls.DOMElementHelper.off(elem, "touchstart.longTouch_" + ctx);
        cls.DOMElementHelper.off(elem, "touchend.longTouch_" + ctx);
      }
    }
  }

  cls.DOMElementHelper = DOMElementHelper;
});
